import { FC, useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import {
    Alert,
    Button,
    Empty,
    MenuProps,
    Modal,
    Result,
    Space,
    Spin,
    Typography,
    Row,
} from "../../design-system";
import {
    ProjectAnalysisStatus,
    getLatestAnalysis,
    canDo,
    ProjectAction,
    getAnalysisByVersion,
    ProjectStatus,
    ProjectType,
    AnalysisOutputType,
    AnalysisType,
    isDealProject,
} from "../../types/ProjectTypes";
import { buildBriefIntakePath } from "../buyerbrief/briefIntakeParams";
import { isSuperAdmin } from "../../types/UserGroup";
import { getProject } from "../../common/api/ProjectClient";
import { useUserState } from "../../common/UserContext";
import InfinitySpinner from "../../common/components/InfinitySpinner";
import { ModalSaveAnalysisOutput } from "./components/ModalSaveAnalysisOutput";
import { ModalRequestChange } from "./components/ModalRequestChange";
import { ProjectCard } from "./components/ProjectCard";
import { InputEditor } from "./components/InputEditor";
import { ModalGuidedIntake } from "./components/ModalGuidedIntake";
import { InputViewer } from "./components/InputViewer";
import { DealOutput } from "./components/DealOutput";
import { RfxOutput } from "./components/RfxOutput";
import { FlexibleOutput } from "./components/FlexibleOutput";
import { CompletedRfxView } from "./components/CompletedRfxView";
import { SourceBanner } from "./components/SourceBanner";
import styles from "./ProjectView.module.less";
import { ProjectViewState } from "./ProjectViewState";

export const ProjectView: FC = () => {
    const navigate = useNavigate();
    const { me } = useUserState();
    const { projectId, version } = useParams<{ projectId: string; version?: string }>();
    const [state, setState] = useState<ProjectViewState | null>(null);
    const [isLoading, setIsLoading] = useState(true);
    const [requestChangeModalOpen, setRequestChangeModalOpen] = useState(false);
    const [saveOutputModalOpen, setSaveOutputModalOpen] = useState(false);
    const [inputModalOpen, setInputModalOpen] = useState(false);
    const [guidedIntakeOpen, setGuidedIntakeOpen] = useState(false);

    const isUserSuperAdmin = isSuperAdmin(me.roles || []);

    // Fetch the project from the URL param and update the local state.
    // Keeps the previous state mounted while refetching so in-place updates
    // (e.g. toggling a vendor selection) don't unmount the screen.
    const fetchProject = async () => {
        try {
            const project = await getProject(Number(projectId));
            const analysis =
                (version && getAnalysisByVersion(project, Number(version))) ||
                getLatestAnalysis(project);
            setState({ project, analysis });
        } catch (error) {
            setState(null); // we will show "Project Not Found"
        } finally {
            setIsLoading(false);
        }
    };

    // Refetch the project when projectId changes. Reset state + spinner so the
    // previous project's content doesn't briefly flash while the new one loads.
    useEffect(() => {
        setIsLoading(true);
        setState(null);
        fetchProject();
    }, [projectId]);

    // Re-derive state when version changes (no fetch needed)
    useEffect(() => {
        if (state) {
            const analysis = version
                ? getAnalysisByVersion(state.project, Number(version)) ||
                  getLatestAnalysis(state.project)
                : getLatestAnalysis(state.project);
            setState({ project: state.project, analysis });
        }
    }, [version]);

    // Sync URL to match state
    useEffect(() => {
        if (state) {
            let path = "";
            if (state.project.status === ProjectStatus.DRAFT) {
                path = `/project/${projectId}/intake`;
            } else {
                path = `/project/${projectId}/analysis/${state.analysis.version}`;
            }
            navigate(path, { replace: true });
        }
    }, [state]);

    if (isLoading) {
        return (
            <div className={styles.container} style={{ textAlign: "center", paddingTop: 100 }}>
                <Spin size="large" />
            </div>
        );
    }

    if (!state) {
        return (
            <div className={styles.notFound}>
                <Empty description="Project not found" />
            </div>
        );
    }

    const { project, analysis } = state;
    const { status } = analysis;

    // ── Intake (project.status = draft) ─────────────────────────

    const renderIntake = () => {
        const canEdit = canDo(project, ProjectAction.EDIT);
        // A guided-intake draft: the page says the intake is unfinished, and
        // editors can open the conversation modal from there. Viewers only
        // see the message.
        if (analysis.inputs?.[0]?.guidedIntake) {
            return (
                <div className={styles.tabContent}>
                    <Result
                        subTitle="The guided intake for this project isn't finished yet."
                        extra={
                            canEdit && (
                                <Button type="primary" onClick={() => setGuidedIntakeOpen(true)}>
                                    Continue intake
                                </Button>
                            )
                        }
                    />
                    {guidedIntakeOpen && (
                        <ModalGuidedIntake
                            open
                            projectId={project.id}
                            onClose={() => setGuidedIntakeOpen(false)}
                            onSubmitted={async (submittedProjectId) => {
                                // Refetch so the now-active project re-renders as the
                                // analysis instead of this intake modal (this view stays
                                // mounted across the route change: projectId is unchanged).
                                await fetchProject();
                                navigate(`/project/${submittedProjectId}/analysis/1`);
                            }}
                        />
                    )}
                </div>
            );
        }
        return (
            <>
                <Row align="middle" justify="space-between" className={styles.contentHeader}>
                    <span className={styles.contentTitle}>Intake</span>
                </Row>
                <div className={styles.tabContent}>
                    <InputEditor
                        state={state}
                        readOnly={!canEdit}
                        onSaveDraft={fetchProject}
                        onSubmit={async () => {
                            await fetchProject();
                            navigate(`/project/${project.id}`);
                        }}
                    />
                </div>
            </>
        );
    };

    // ── Analysis (project.status = active) ──────────────────────────

    // admin actions
    const adminActionsMenuItems: MenuProps["items"] = [];
    if (isUserSuperAdmin) {
        if (
            status === ProjectAnalysisStatus.REQUIRE_USER_ACTION ||
            status === ProjectAnalysisStatus.SUBMITTED
        ) {
            adminActionsMenuItems.push({
                key: "RequestChanges",
                label: "Request changes",
                onClick: () => setRequestChangeModalOpen(true),
            });
        }
        if (status !== ProjectAnalysisStatus.DRAFT) {
            adminActionsMenuItems.push({
                key: "SaveAnalysisOutput",
                label: "Save/Edit analysis output",
                onClick: () => setSaveOutputModalOpen(true),
            });
        }
        // Analysts generate a brief when they deliver a completed deal analysis,
        // so start it from here with the vendor already known. Needs a vendor to
        // pre-fill from: orderForm is optional and drafts may not have one.
        if (isDealProject(project) && project.orderForm?.vendor?.name) {
            adminActionsMenuItems.push({
                key: "GenerateBuyerBrief",
                label: "Generate Buyer Brief",
                onClick: () => navigate(buildBriefIntakePath(project.orderForm!.vendor!)),
            });
        }
    }

    const renderOutputContent = () => {
        // "Complete" analysis (closing without output) — show the input
        if (analysis.type === AnalysisType.COMPLETE) {
            const firstInput = analysis.inputs?.[0];
            const isCompletedRfx =
                project.type === ProjectType.RFX && project.status === ProjectStatus.COMPLETED;

            // A completed RFx shows its vendor outcome, then the closing
            // survey as submitted.
            if (isCompletedRfx) {
                return (
                    <div className={styles.tabContent}>
                        <Space direction="vertical" size="large" fullWidth>
                            <CompletedRfxView project={project} onUpdate={fetchProject} />
                            {firstInput && <InputViewer input={firstInput} />}
                        </Space>
                    </div>
                );
            }
            if (firstInput) {
                return (
                    <div className={styles.tabContent}>
                        <InputViewer input={firstInput} />
                    </div>
                );
            }
            return (
                <div className={styles.tabContent}>
                    <Empty description="No analysis available" />
                </div>
            );
        }

        // Show the report once there is one, to everyone when the analysis is
        // completed and to superadmins as a preview before that.
        if (
            analysis.hasReport &&
            (status === ProjectAnalysisStatus.COMPLETED || isUserSuperAdmin)
        ) {
            // A completed RFx keeps its vendor outcome above its savings report.
            const completedRfx =
                project.type === ProjectType.RFX &&
                project.status === ProjectStatus.COMPLETED &&
                analysis.type === AnalysisType.ROI;
            switch (analysis.outputType) {
                case AnalysisOutputType.DEAL:
                    return <DealOutput project={project} analysis={analysis} />;
                case AnalysisOutputType.RFX:
                    return <RfxOutput project={project} analysis={analysis} />;
                case AnalysisOutputType.FLEXIBLE:
                    if (completedRfx) {
                        return (
                            <>
                                <div className={styles.legacyContentContainer}>
                                    <CompletedRfxView project={project} onUpdate={fetchProject} />
                                </div>
                                <FlexibleOutput project={project} analysis={analysis} />
                            </>
                        );
                    }
                    return <FlexibleOutput project={project} analysis={analysis} />;
            }
        }

        // The analyst sent the intake back, or an update is still drafted:
        // say so, and let editors open the editor from here. Like a draft,
        // nothing opens on its own, and viewers only see the message.
        if (
            status === ProjectAnalysisStatus.REQUIRE_USER_ACTION ||
            status === ProjectAnalysisStatus.DRAFT
        ) {
            const changesRequested = status === ProjectAnalysisStatus.REQUIRE_USER_ACTION;
            return (
                <div className={styles.tabContent}>
                    <Result
                        status={changesRequested ? "warning" : "info"}
                        title={changesRequested ? "Changes requested" : "Update not submitted"}
                        subTitle={
                            changesRequested
                                ? analysis.requireUserActionMessage
                                : "This analysis update hasn't been submitted yet."
                        }
                        extra={
                            canDo(project, ProjectAction.EDIT) && (
                                <Button type="primary" onClick={() => setInputModalOpen(true)}>
                                    {changesRequested ? "Update analysis" : "Continue update"}
                                </Button>
                            )
                        }
                    />
                </div>
            );
        }

        // Otherwise there is no report to show yet: the analysis is pending,
        // whatever its status says. A signed deal waits for its savings
        // report, which takes no further input from the user.
        const savingsReport = analysis.type === AnalysisType.ROI;
        return (
            <div className={styles.tabContent}>
                <Result
                    icon={<InfinitySpinner size={64} spin={true} />}
                    subTitle={
                        <>
                            {savingsReport
                                ? "Congratulations on signing. We're preparing your savings report."
                                : "We're reviewing your documents and preparing your negotiation playbook."}
                            <br />
                            You'll be notified when it's ready.
                        </>
                    }
                />
                {!savingsReport && canDo(project, ProjectAction.EDIT) && (
                    <div className={styles.pendingUpdateHint}>
                        Need to make changes?{" "}
                        <Button type="link" size="small" onClick={() => setInputModalOpen(true)}>
                            Update your analysis
                        </Button>
                    </div>
                )}
            </div>
        );
    };

    // ── Render ───────────────────────────────────────────────────

    // The flexible report draws its own full-width frame; everything else
    // (intake, pending states, closing analyses with their snapshot output,
    // the completed RFx view) sits in the margined container.
    const showsFlexibleReport =
        project.status !== ProjectStatus.DRAFT &&
        analysis.outputType === AnalysisOutputType.FLEXIBLE &&
        analysis.type !== AnalysisType.COMPLETE &&
        !!analysis.hasReport &&
        (status === ProjectAnalysisStatus.COMPLETED || isUserSuperAdmin);
    const content = project.status === ProjectStatus.DRAFT ? renderIntake() : renderOutputContent();

    return (
        <div className={styles.container}>
            {project.source && <SourceBanner source={project.source} />}

            <div className={styles.stickyHeader}>
                <ProjectCard
                    project={project}
                    selectedVersion={analysis.version}
                    onAnalysisSelect={(v) => navigate(`/project/${project.id}/analysis/${v}`)}
                    onAnalysisUpdate={() => setInputModalOpen(true)}
                    onUpdate={fetchProject}
                    showActions
                    adminActionsMenuItems={adminActionsMenuItems}
                    noMarginBottom
                />
            </div>

            <div key={analysis.version}>
                {showsFlexibleReport ? (
                    content
                ) : (
                    <div className={styles.legacyContentContainer}>{content}</div>
                )}
            </div>

            <ModalRequestChange
                open={requestChangeModalOpen}
                state={state}
                onClose={() => setRequestChangeModalOpen(false)}
                onSuccess={fetchProject}
            />

            <ModalSaveAnalysisOutput
                open={saveOutputModalOpen}
                state={state}
                onClose={() => setSaveOutputModalOpen(false)}
                onSuccess={fetchProject}
            />

            <Modal
                open={inputModalOpen}
                title={
                    status === ProjectAnalysisStatus.SUBMITTED ||
                    status === ProjectAnalysisStatus.REQUIRE_USER_ACTION
                        ? "Update analysis"
                        : "Request new analysis"
                }
                footer={null}
                width={800}
                destroyOnClose
                onCancel={() => setInputModalOpen(false)}
            >
                <Typography.Text
                    type="secondary"
                    style={{ fontSize: 13, display: "block", marginBottom: 16 }}
                >
                    Add/remove documents and describe what changed.
                </Typography.Text>
                {status === ProjectAnalysisStatus.REQUIRE_USER_ACTION && (
                    <Alert
                        message="Changes requested"
                        description={analysis.requireUserActionMessage}
                        type="warning"
                        showIcon
                        style={{ marginBottom: 16 }}
                    />
                )}
                <InputEditor
                    state={{ project, analysis: getLatestAnalysis(project) }}
                    onSaveDraft={fetchProject}
                    onSubmit={async () => {
                        setInputModalOpen(false);
                        await fetchProject();
                        navigate(`/project/${project.id}`);
                    }}
                />
            </Modal>
        </div>
    );
};
